import ReactEcharts from 'echarts-for-react';
import { getWithToken } from "../fetchWithToken";
import { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";

export const YearlyProfit = ({ year }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getWithToken('/api/statistic/profit/', { 'year': year })
            .then((result) => {
                if (result && result.profit && result.income && result.expense) {
                    setData({
                        profit: result.profit.map(num => parseFloat(num.toFixed(0))),
                        income: result.income.map(num => parseFloat(num.toFixed(0))),
                        expense: result.expense.map(num => parseFloat(num.toFixed(0))),
                        accumulatedProfit: result.profit.reduce((acc, value) => {
                            if (acc.length > 0) {
                                acc.push(acc[acc.length - 1] + value);
                            } else {
                                acc.push(value);
                            }
                            return acc;
                        }, [])
                    });
                }
            })
            .catch(err => {
                console.error('获取收支数据失败:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [year]);

    const getOption = () => {
        if (!data) return null;

        return {
            backgroundColor: 'transparent',
            title: {
                text: '年度收支趋势分析',
                left: '1%',
                top: '2%',
                textStyle: { fontSize: 16, fontWeight: 600, color: '#1f2937' }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                textStyle: { color: '#1f2937' },
                formatter: (params) => {
                    let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].name}</div>`;
                    params.forEach(param => {
                        const colorBox = `<span style="display:inline-block;margin-right:8px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
                        const value = Math.abs(param.value).toLocaleString();
                        result += `<div style="margin: 4px 0;">${colorBox}${param.seriesName}: <strong>¥${value}</strong></div>`;
                    });
                    return result;
                }
            },
            legend: {
                data: ['净收入', '支出', '收入', '累计储蓄'],
                top: '2%',
                right: '2%',
                itemGap: 15,
                textStyle: { fontSize: 12, color: '#6b7280' }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '5%',
                top: '12%',
                containLabel: true
            },
            xAxis: [{
                type: 'category',
                axisTick: { show: false },
                axisLine: { lineStyle: { color: '#e5e7eb' } },
                axisLabel: { color: '#6b7280', fontSize: 12 },
                data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
            }],
            yAxis: [{
                type: 'value',
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
                axisLabel: {
                    color: '#9ca3af',
                    fontSize: 12,
                    formatter: '¥{value}'
                }
            }],
            series: [
                {
                    name: '净收入',
                    type: 'bar',
                    barWidth: 15,
                    itemStyle: {
                        color: '#3b82f6',
                        borderRadius: (params) => {
                            const val = params.value;
                            if (val >= 0) {
                                return [4, 4, 0, 0];  // 正值圆角在顶部
                            } else {
                                return [0, 0, 4, 4];  // 负值圆角在底部
                            }
                        }
                    },
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#3b82f6',
                        formatter: (params) => {
                            const val = params.value;
                            if (val === 0) return '';
                            return val > 0 ? `+${val}` : val;
                        }
                    },
                    data: data.profit
                },
                {
                    name: '收入',
                    type: 'bar',
                    barWidth: 15,
                    itemStyle: {
                        color: '#ef4444',  // 收入改为红色
                        borderRadius: [6, 6, 0, 0]
                    },
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#ef4444',
                        formatter: (params) => {
                            if (params.value === 0) return '';
                            return `¥${params.value}`;
                        }
                    },
                    data: data.income
                },
                {
                    name: '支出',
                    type: 'bar',
                    barWidth: 15,
                    itemStyle: {
                        color: '#10b981',  // 支出改为绿色
                        borderRadius: [0, 0, 6, 6]  // 圆角在底部
                    },
                    label: {
                        show: true,
                        position: 'bottom',
                        fontSize: 12,
                        color: '#10b981',
                        formatter: (params) => {
                            if (params.value === 0) return '';
                            return `¥${Math.abs(params.value)}`;
                        }
                    },
                    data: data.expense
                },
                {
                    name: '累计储蓄',
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: {
                        width: 2,
                        color: '#8b5cf6'
                    },
                    itemStyle: { color: '#8b5cf6' },
                    data: data.accumulatedProfit
                }
            ]
        };
    };

    if (loading || !data) {
        return (
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', mb: 3 }}>
                <CardContent sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">加载中...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            mb: 3,
            transition: 'all 0.3s ease',
            overflow: 'visible',
            '&:hover': {
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }
        }}>
            <CardContent sx={{ p: 2 }}>
                <ReactEcharts option={getOption()} style={{ height: '500px' }} />
            </CardContent>
        </Card>
    );
};
